import { IntlShape } from 'react-intl';
import {
  MEDIA_WIDGET_REEL_VARIANTS,
  MediaWidgetFileKind,
  MediaWidgetReelVariant,
  MediaWidgetSource,
  SerializedMediaWidgetData,
} from 'fym-shared/src/smartlinks/mediaWidget';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { CatalogItem } from '../../../../../../redux/api/fymAPI.types';
import { catalogArtworkSrc } from '../../../../catalog/catalogArtwork';
import { PromoteMediaDraft } from '../../../../../music24/promote/draftStore';
import { mediaFactsLine } from '../../../../../music24/promote/composer/composerMediaText';
import { PickerFile, pickerAspectLabel } from '../../../../../music24/promote/composer/picker/pickerFiles';
import { revisionCardLabel } from '../../../../../music24/promote/releases/assets/cardLabel';
import { mediaBlockLabel } from '../mediaBlockLabel';
import { mediaBlockMessages } from '../mediaBlockMessages';

/**
 * The file that the rail shows as picked (design a2, a4): the source that the block stores, and the
 * name, the facts and the picture of the file now. Pure data, so it is tested without React.
 */
export interface MediaBlockPick {
  source: MediaWidgetSource;
  kind: MediaWidgetFileKind;
  label: string;
  /** "9:16 · Video · 0:15". */
  facts: string;
  /** Absolute url, or null. */
  pictureUrl: string | null;
  /** The revision now on the page (r2); null for an upload. */
  revision: number | null;
}

function isReelVariant(value: string): value is MediaWidgetReelVariant {
  return (MEDIA_WIDGET_REEL_VARIANTS as readonly string[]).includes(value);
}

/**
 * The source of a picker tile. A Studio file never gives its file id: the block follows the newest
 * ready revision of the asset type and variant, or of the reel length (owner answer 2026-10-08).
 * Null for a reel of a length that a block cannot follow.
 */
export function mediaSourceOfPickerFile(file: PickerFile, catalogItemUuid: string): MediaWidgetSource | null {
  if (file.card.kind === 'reel') {
    return isReelVariant(file.card.variant) ? { kind: 'reel', catalogItemUuid, reelVariant: file.card.variant } : null;
  }
  return { kind: 'kitAsset', catalogItemUuid, themeId: file.card.themeId, variant: file.card.variant };
}

export function pickOfPickerFile(intl: IntlShape, file: PickerFile, catalogItemUuid: string): MediaBlockPick | null {
  const source = mediaSourceOfPickerFile(file, catalogItemUuid);
  if (source === null) {
    return null;
  }
  return {
    source,
    kind: file.mediaKind,
    label: revisionCardLabel(intl, file.card),
    facts: mediaFactsLine(intl, {
      kind: file.mediaKind,
      aspect: pickerAspectLabel(file),
      durationSec: file.durationSec,
    }),
    pictureUrl: file.pictureUrl === null ? null : getFullAPIFileURL(file.pictureUrl),
    revision: file.revision,
  };
}

/**
 * The cover of a song as a pick (owner request 2026-10-09): the block follows the cover of the
 * catalog row, so a new cover (an upload, the cover editor) shows on the page with no edit. Null
 * for a song with no cover.
 */
export function pickOfCover(
  intl: IntlShape,
  item: Pick<CatalogItem, 'uuid' | 'artworkUrl' | 'artworkFile'>
): MediaBlockPick | null {
  const pictureUrl = catalogArtworkSrc(item, 160);
  if (pictureUrl === undefined) {
    return null;
  }
  return {
    source: { kind: 'cover', catalogItemUuid: item.uuid },
    kind: 'image',
    label: intl.formatMessage(mediaBlockMessages.cover),
    facts: mediaFactsLine(intl, { kind: 'image', aspect: '1:1', durationSec: null }),
    pictureUrl,
    revision: null,
  };
}

/** The pick of a file that the artist uploaded (an image or a video). */
export function pickOfUpload(intl: IntlShape, media: PromoteMediaDraft): MediaBlockPick {
  const kind = media.kind ?? 'video';
  return {
    source: { kind: 'upload', fileId: media.fileId },
    kind,
    label: media.label ?? '',
    facts: mediaFactsLine(intl, { kind, aspect: media.aspect, durationSec: media.durationSec }),
    pictureUrl: media.thumbnailUrl ?? (kind === 'image' ? media.fileUrl : null),
    revision: null,
  };
}

/** The pick of a saved block, or null when its file is gone (the rail then asks for another file). */
export function pickOfWidget(intl: IntlShape, data: SerializedMediaWidgetData): MediaBlockPick | null {
  const { media } = data;
  if (media === null) {
    return null;
  }
  return {
    source: data.source,
    kind: media.kind,
    label: mediaBlockLabel(intl, data),
    facts: mediaFactsLine(intl, {
      kind: media.kind,
      aspect: pickerAspectLabel({ width: media.width, height: media.height }),
      durationSec: media.durationSec,
    }),
    pictureUrl: media.thumbUrl ?? media.posterUrl ?? (media.kind === 'image' ? media.url : null),
    revision: media.revision,
  };
}
